import { useCallback, useMemo, useState } from 'react'

import { useLinkTo } from '@react-navigation/native'
import { useDisclose, useToast } from 'native-base'
import { Toast } from 'organisms'
import { Platform } from 'react-native'
import {
  TOpportunitiesV3EvaluationPayload,
  OPPORTUNITIES_TYPES,
  OPPORTUNITY_TYPE_ENUM,
  useCreateOpportunitiesUnlinkRequestMutation,
  useCreateOpportunitiesEvaluationMutation,
  useGetOpportunitiesV3DetailsQuery
} from 'src/integration/resources/opportunitiesV3'

import { OpportunityDeletionSubmitData, OPPORTUNITY_DELETION_OTHER_VALUE } from './Deletion/types'
import { buildEvaluationPayload } from './Evaluation/EvaluationModal.helpers'
import { EvaluationFormValues } from './Evaluation/EvaluationModal.types'
import { UseOpportunitiesDetailsScreen } from './OpportunitiesDetailsScreen.types'

export const useOpportunitiesDetailsScreen: UseOpportunitiesDetailsScreen = ({
  navigation,
  route
}) => {
  const opportunityId = route?.params?.id ?? ''

  const normalizeRouteType = (type?: unknown) => {
    if (type === OPPORTUNITY_TYPE_ENUM.ACTIVE_BASE || type === OPPORTUNITY_TYPE_ENUM.ODC) {
      return type
    }

    if (type === OPPORTUNITIES_TYPES.ACTIVE_BASE || type === 2) {
      return OPPORTUNITY_TYPE_ENUM.ACTIVE_BASE
    }

    return OPPORTUNITY_TYPE_ENUM.ODC
  }

  const opportunityType = normalizeRouteType(route?.params?.type)

  const { data, isLoading } = useGetOpportunitiesV3DetailsQuery(opportunityId, !!opportunityId)

  const opportunityDetails = useMemo(() => data?.data.data, [data?.data.data])

  const {
    isOpen: deleteOpportunityModalIsOpen,
    onOpen: handleOpenDeleteOpportunityModal,
    onClose: handleCloseDeleteOpportunityModal
  } = useDisclose()

  const {
    isOpen: deletionSuccessModalIsOpen,
    onOpen: handleOpenDeletionSuccessModal,
    onClose: handleCloseDeletionSuccessModal
  } = useDisclose()

  const [requestDeletionIsLoading, setRequestDeletionIsLoading] = useState(false)

  const toast = useToast()

  const linkTo = useLinkTo()

  const { mutateAsync: createOpportunitiesUnlinkRequest } =
    useCreateOpportunitiesUnlinkRequestMutation()

  const { mutateAsync: createOpportunitiesEvaluation } = useCreateOpportunitiesEvaluationMutation()

  const handleSubmitOpportunityDeletion = useCallback(
    async ({ reason, description, attachments }: OpportunityDeletionSubmitData) => {
      if (!opportunityId) {
        return
      }

      setRequestDeletionIsLoading(true)

      try {
        const formattedDescription = description?.trim()

        await createOpportunitiesUnlinkRequest({
          opportunity_id: opportunityId,
          justification:
            reason === OPPORTUNITY_DELETION_OTHER_VALUE ? formattedDescription ?? '' : reason,
          attachments: attachments.map(
            ({ storage_file_name, file_name, url, size, created_at }) => ({
              file_name_presigned: storage_file_name,
              file_name,
              file_url: url,
              file_size: size,
              submit_date: created_at
            })
          )
        })

        handleCloseDeleteOpportunityModal()

        handleOpenDeletionSuccessModal()
      } catch (error) {
        toast.show({
          render: () => (
            <Toast type="error" text="Não foi possível solicitar a exclusão da oportunidade" />
          ),
          duration: 3000
        })
      } finally {
        setRequestDeletionIsLoading(false)
      }
    },
    [
      createOpportunitiesUnlinkRequest,
      handleCloseDeleteOpportunityModal,
      handleOpenDeletionSuccessModal,
      opportunityId,
      toast
    ]
  )

  const handleGoBack = useCallback(() => {
    return navigation.canGoBack() ? navigation.goBack() : navigation.navigate('OpportunitiesList')
  }, [navigation])

  const handleOpenTabDetails = useCallback(() => {
    const tabNumber = opportunityDetails?.store?.tab_number

    if (!tabNumber) return

    const path = `/carteira/tabs/${tabNumber}`

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(path, '_blank', 'noopener')

      return
    }

    linkTo(path)
  }, [linkTo, opportunityDetails?.store?.tab_number])

  const {
    isOpen: evaluationModalIsOpen,
    onClose: handleCloseEvaluationModal,
    onOpen: handleOpenEvaluationModal
  } = useDisclose()

  const handleSubmitEvaluation = useCallback(
    async (values: EvaluationFormValues) => {
      if (!opportunityId) {
        return
      }

      try {
        const { priority, tab, competitors } = buildEvaluationPayload(values)

        const payload: TOpportunitiesV3EvaluationPayload = {
          opportunity_id: opportunityId,
          ...(Object.keys(priority).length > 0 ? { priority } : {}),
          ...(Object.keys(tab).length > 0 ? { tab } : {}),
          ...(competitors.length > 0 ? { competitors } : {})
        }

        await createOpportunitiesEvaluation(payload, {
          onSuccess: () => {
            toast.show({
              render: () => <Toast type="success" text="Avaliação enviada com sucesso 🎉" />,
              placement: 'bottom',
              duration: 1500
            })
          },
          onError: () => {
            toast.show({
              render: () => <Toast type="error" text="Não foi possível enviar a avaliação" />,
              duration: 3000
            })
          }
        })
      } catch (error) {
        toast.show({
          render: () => <Toast type="error" text="Não foi possível enviar a avaliação" />,
          duration: 3000
        })
      }
    },
    [createOpportunitiesEvaluation, opportunityId, toast]
  )

  return {
    opportunityId,
    opportunityType,
    opportunityDetails,
    isLoadingOpportunityDetails: isLoading,
    deleteOpportunityModalIsOpen,
    deletionSuccessModalIsOpen,
    requestDeletionIsLoading,
    handleOpenDeleteOpportunityModal,
    handleCloseDeleteOpportunityModal,
    handleSubmitOpportunityDeletion,
    handleCloseDeletionSuccessModal,
    handleGoBack,
    handleOpenTabDetails,
    evaluationModalIsOpen,
    handleCloseEvaluationModal,
    handleOpenEvaluationModal,
    handleSubmitEvaluation
  }
}
